import React from 'react';
import { KeyValueRow } from '..';

type UnknownRecord = Record<string, unknown>;

// ===== helpers =====

const toTitle = (s: string) => s
  .replace(/[_-]/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^./, (c) => c.toUpperCase());

const isPlainObject = (v: unknown): v is UnknownRecord => !!v
  && typeof v === 'object'
  && !Array.isArray(v)
  && !(v instanceof Date)
  && !(v instanceof Map)
  && !(v instanceof Set);

const fmt = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '-';
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? String(v) : v.toISOString();
  }
  if (typeof v === 'bigint') return `${v.toString()}n`;
  if (v instanceof RegExp) return v.toString();

  return String(v);
};

const isPrimitiveLike = (v: unknown) => v === null
  || v === undefined
  || typeof v !== 'object'
  || v instanceof Date
  || v instanceof RegExp;

const makeLabel = (keyLabel?: string, fallback?: string) => {
  if (keyLabel) return `${toTitle(keyLabel)}:`;
  if (fallback) return `${fallback}:`;

  return '';
};

const emptyMark = (node: unknown) => {
  if (Array.isArray(node)) return '[]';
  if (node instanceof Set) return 'Set(0)';
  if (node instanceof Map) return 'Map(0)';

  return '{{}}';
};

// универсальные пары [label, value]
function entriesOf(
  node: unknown,
  sortKeys: boolean,
  maxArray?: number,
): Array<[string, unknown]> | null {
  if (Array.isArray(node)) {
    const arr = node as unknown[];
    const slice = typeof maxArray === 'number' ? arr.slice(0, maxArray) : arr;

    return slice.map((v, i): [string, unknown] => [`[${i}]`, v]);
  }

  if (node instanceof Set) {
    const items = Array.from(node.values()) as unknown[];
    const slice = typeof maxArray === 'number' ? items.slice(0, maxArray) : items;

    return slice.map((v, i): [string, unknown] => [`[${i}]`, v]);
  }

  if (node instanceof Map) {
    const m = Array.from(node.entries()) as Array<[unknown, unknown]>;

    return m.map(([k, v]): [string, unknown] => [String(k), v]);
  }

  if (isPlainObject(node)) {
    const e = Object.entries(node as UnknownRecord);

    return sortKeys ? [...e].sort(([a], [b]) => a.localeCompare(b)) : e;
  }

  return null;
}

const truncationTail = (node: unknown, maxArray?: number) => {
  if (typeof maxArray !== 'number') return null;

  let size: number | undefined;

  if (Array.isArray(node)) {
    size = node.length;
  } else if (node instanceof Set) {
    size = node.size;
  }

  if (size === undefined || size <= maxArray) return null;

  return <KeyValueRow label="…" value={`+${size - maxArray} more`} />;
};

// ===== main component =====

type KVProps = {
  data: unknown;
  label?: string;
  sortKeys?: boolean;
  maxArray?: number;
  maxDepth?: number;
  className?: string;
};

// eslint-disable-next-line import/prefer-default-export
export function KV({
  data,
  label,
  sortKeys = true,
  maxArray,
  maxDepth = 100,
  className,
}: KVProps) {
  const seen = new WeakSet<object>();

  const render = (
    node: unknown,
    keyLabel?: string,
    depth = 0,
  ): React.ReactNode => {
    if (depth > maxDepth) {
      const cap = toTitle(keyLabel ?? 'Value');

      return (
        <KeyValueRow label={`${cap}:`} value="… (max depth reached)" />
      );
    }

    if (isPrimitiveLike(node)) {
      const lbl = makeLabel(keyLabel, label);

      return <KeyValueRow label={lbl} value={fmt(node)} />;
    }

    // защита от циклов
    if (seen.has(node as object)) {
      const cap = toTitle(keyLabel ?? 'Value');

      return <KeyValueRow label={`${cap}:`} value="[Circular]" />;
    }
    seen.add(node as object);

    const pairs = entriesOf(node, sortKeys, maxArray);

    // не контейнер (класс/функция и т.д.)
    if (!pairs) {
      const cap = toTitle(keyLabel ?? 'Value');

      return <KeyValueRow label={`${cap}:`} value={String(node)} />;
    }

    // пустой контейнер
    if (pairs.length === 0) {
      const cap = toTitle(keyLabel ?? 'Value');

      return <KeyValueRow label={`${cap}:`} value={emptyMark(node)} />;
    }

    // контейнер
    return (
      <div className="kv-cols" style={{ marginLeft: depth ? 12 : 0 }}>
        {keyLabel && (
          <div className="kv-row kv-key">{`${toTitle(keyLabel)}:`}</div>
        )}
        <div>
          {pairs.map(([k, v]) => (isPrimitiveLike(v) ? (
            <KeyValueRow key={k} label={`${toTitle(k)}:`} value={fmt(v)} />
          ) : (
            <div className="kv-cols" key={k}>
              <div className="kv-row kv-key">{`${toTitle(k)}:`}</div>
              <div>{render(v, undefined, depth + 1)}</div>
            </div>
          )))}
          {(Array.isArray(node) || node instanceof Set)
            && truncationTail(node, maxArray)}
        </div>
      </div>
    );
  };

  return (
    <div className={`kv-list ${className ?? ''}`}>
      {render(data, label, 0)}
    </div>
  );
}

KV.defaultProps = {
  label: undefined,
  sortKeys: undefined,
  maxArray: undefined,
  maxDepth: undefined,
  className: undefined,
};
